import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  useTheme,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { CATEGORIES } from '../utils/categoryColors';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { SUPPORTED_CURRENCIES } from '../utils/currency';
import { BILLING_CYCLES, calculateNextRenewalDate, BillingCycle } from '../utils/billingCycleUtils';
import { getCurrencySymbol } from '../utils/currency';

const AddSubscription: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { getMainCurrency } = useUserPreferences(user?.id);
  const { getPaymentMethodNames } = usePaymentMethods(user?.id);
  
  const [formData, setFormData] = useState({
    name: '',
    cost: 0,
    billing_cycle: 'monthly' as BillingCycle,
    category: 'Entertainment',
    payment_method: '',
    notes: '',
    currency: getMainCurrency(), // Initialize with user's main currency
    start_date: new Date().toISOString().split('T')[0], // Initialize with today's date
    next_renewal_date: format(new Date(), 'yyyy-MM-dd'),
    is_trial: false,
    trial_end_date: '',
    is_shared: false,
    shared_with: [] as string[],
    tags: [] as string[],
  });

  const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
  const [newPaymentMethodType, setNewPaymentMethodType] = useState('credit_card');
  const [tagInput, setTagInput] = useState('');

  // Reset new payment method name when payment method selection changes
  useEffect(() => {
    if (formData.payment_method !== '__add_new__') {
      setNewPaymentMethodName('');
      setNewPaymentMethodType('credit_card');
    }
  }, [formData.payment_method]);

  // Auto-calculate next renewal date when billing cycle or start date changes
  useEffect(() => {
    if (formData.start_date && formData.billing_cycle) {
      const startDate = new Date(formData.start_date);
      const nextRenewal = calculateNextRenewalDate(startDate, formData.billing_cycle);
      setFormData(prev => ({
        ...prev,
        next_renewal_date: format(nextRenewal, 'yyyy-MM-dd'),
      }));
    }
  }, [formData.start_date, formData.billing_cycle]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTagInputKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && tagInput.trim()) {
      event.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to add a subscription');
      return;
    }

    try {
      // Handle new payment method
      let finalPaymentMethod = formData.payment_method;
      if (formData.payment_method === '__add_new__') {
        if (!newPaymentMethodName.trim()) {
          toast.error('Please enter a payment method name');
          return;
        }
        finalPaymentMethod = newPaymentMethodName.trim();
        
        // Create the new payment method first
        const { error: pmError } = await supabase
          .from('payment_methods')
          .insert({
            user_id: user.id,
            name: newPaymentMethodName.trim(),
            type: newPaymentMethodType,
            is_active: true,
          });

        if (pmError) throw pmError;
      }

      // Get the user's main currency
      const { error } = await supabase
        .from('subscriptions')
        .insert({
          user_id: user.id,
          name: formData.name,
          cost: parseFloat(formData.cost.toString()),
          currency: formData.currency || getMainCurrency(), // Use selected currency or fallback to main currency
          billing_cycle: formData.billing_cycle,
          category: formData.category,
          payment_method: finalPaymentMethod,
          notes: formData.notes,
          start_date: formData.start_date,
          next_renewal_date: formData.next_renewal_date,
          is_trial: formData.is_trial,
          trial_end_date: formData.trial_end_date || null,
          is_shared: formData.is_shared,
          shared_with: formData.is_shared ? formData.shared_with : [],
          tags: formData.tags,
          is_active: true,
        });

      if (error) throw error;

      toast.success('Subscription added successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to add subscription');
      console.error('Error adding subscription:', error);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Add New Subscription
        </Typography>
        
        <Typography variant="body1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          Track your new subscription with all the details you need.
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                placeholder="e.g., Netflix, Spotify"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={formData.cost}
                onChange={(e) => handleInputChange('cost', e.target.value)}
                required
                placeholder="0.00"
                InputProps={{
                  startAdornment: getCurrencySymbol(formData.currency || getMainCurrency()),
                }}
                helperText={`Cost in ${formData.currency || getMainCurrency()}`}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency || getMainCurrency()}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  label="Currency"
                >
                  {SUPPORTED_CURRENCIES.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={formData.billing_cycle}
                  onChange={(e) => handleInputChange('billing_cycle', e.target.value as BillingCycle)}
                  label="Billing Cycle"
                >
                  {BILLING_CYCLES.map((cycle) => (
                    <MenuItem key={cycle.value} value={cycle.value}>
                      <Box>
                        <Typography variant="body1">{cycle.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cycle.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.payment_method}
                  label="Payment Method"
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a payment method</em>
                  </MenuItem>
                  {getPaymentMethodNames().map((methodName) => (
                    <MenuItem key={methodName} value={methodName}>
                      {methodName}
                    </MenuItem>
                  ))}
                  <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                    + Add New Payment Method
                  </MenuItem>
                </Select>
              </FormControl>
              {formData.payment_method === '__add_new__' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="New Payment Method Name"
                      value={newPaymentMethodName}
                      onChange={(e) => setNewPaymentMethodName(e.target.value)}
                      placeholder="e.g., My MasterCard, Business PayPal"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={newPaymentMethodType}
                        onChange={(e) => setNewPaymentMethodType(e.target.value)}
                        label="Type"
                      >
                        <MenuItem value="credit_card">Credit Card</MenuItem>
                        <MenuItem value="debit_card">Debit Card</MenuItem>
                        <MenuItem value="paypal">PayPal</MenuItem>
                        <MenuItem value="e_wallet">E-Wallet</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Next Renewal Date"
                type="date"
                value={formData.next_renewal_date}
                onChange={(e) => handleInputChange('next_renewal_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Auto-calculated based on billing cycle, but you can adjust manually"
              />
            </Grid>
            
            {/* Trial Information */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_trial}
                    onChange={(e) => handleInputChange('is_trial', e.target.checked)}
                  />
                }
                label="This is a free trial"
              />
            </Grid>

            {formData.is_trial && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Trial End Date"
                  type="date"
                  value={formData.trial_end_date}
                  onChange={(e) => handleInputChange('trial_end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {/* Shared Subscription */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_shared}
                    onChange={(e) => handleInputChange('is_shared', e.target.checked)}
                  />
                }
                label="This is a shared subscription"
              />
            </Grid>

            {formData.is_shared && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shared With"
                  value={formData.shared_with.join(', ')}
                  onChange={(e) => handleInputChange('shared_with', e.target.value.split(',').map(s => s.trim()))}
                  placeholder="Enter email addresses separated by commas"
                  helperText="Enter email addresses of people you share this subscription with"
                />
              </Grid>
            )}

            {/* Tags */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                placeholder="Press Enter to add tags"
                helperText="Press Enter to add tags"
              />
              {formData.tags.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                multiline
                rows={4}
                placeholder="Add any additional notes about this subscription..."
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ 
                minWidth: 120,
                color: theme.palette.mode === 'dark' ? 'black' : 'white',
                '&:hover': {
                  color: theme.palette.mode === 'dark' ? 'black' : 'white',
                },
              }}
            >
              Add Subscription
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddSubscription; 